import { z } from 'zod';
import { type Folder as FolderType } from '@/types/folder';
import { type Album as AlbumType } from '@/types/album';
import { type Asset as AssetType } from '@/types/asset';
import { warnProd } from '@/utilities/log';

export enum NodeType {
  Assets = 'assets',
  Folders = 'folders',
  Albums = 'albums',
}

export enum DAMNodeType {
  Asset = 'asset',
  Folder = 'folder',
  Album = 'album',
}

/**
 * The two node kinds that hold other nodes and can therefore be browsed into. Distinguished from
 * `DAMNodeType` because "a thing you can navigate to and list children of" is a smaller idea than
 * "any node", and several call sites are only correct for those two.
 */
export type ChildNodeType = DAMNodeType.Folder | DAMNodeType.Album;

/**
 * The singular `DAMNodeType` a node carries, mapped to the plural `NodeType` the panel, toolbar and
 * audit API speak in.
 *
 * One owner for the conversion. It was previously five: an exhaustive `Record` in the audit log plus
 * four hand-written `=== DAMNodeType.Album ? Albums : Folders` ternaries, each of which silently
 * answered "folders" for an asset. Being a `Record` over the whole enum means a new `DAMNodeType`
 * fails to compile here instead of defaulting to folders four times over.
 *
 * Callers holding a possibly-absent type decide their own fallback rather than having one hidden in
 * here — `selectedNode ? toNodeType(selectedNode.type) : NodeType.Folders` says which case it is.
 */
const NODE_TYPE_BY_DAM_NODE_TYPE: Record<DAMNodeType, NodeType> = {
  [DAMNodeType.Asset]: NodeType.Assets,
  [DAMNodeType.Folder]: NodeType.Folders,
  [DAMNodeType.Album]: NodeType.Albums,
};

export const toNodeType = (type: DAMNodeType): NodeType => NODE_TYPE_BY_DAM_NODE_TYPE[type];

export type TheNode = AlbumType | FolderType | AssetType;

export const nodeSchema = z.object({
  id: z.string(),
  slug: z.string(),
  name: z.string(),
  locked: z.boolean().optional(),
  created_at: z.string().optional(),
  updated_at: z.string().optional(),
  parent_id: z.string().nullable().optional(),
});
export type Node = z.infer<typeof nodeSchema>;

export const treeRootNodeSchema = z.object({
  id: z.string(),
  // Validated at the boundary rather than narrowed per consumer. The backend's own NodeType enum
  // (app/Models/Enums/NodeType.php) is exactly these three values and TreeListVO serializes
  // `type->value`, so the wire format is a closed set — parsing it as one means every reader of
  // `TreeRootNode.type` narrows for free instead of re-deriving folder-vs-album from a raw string.
  //
  // Strict on purpose, but not fatal: `tolerantNodeArray` below drops a node this rejects rather
  // than failing the whole response, so a backend that adds a NodeType costs one hidden node
  // instead of the entire sidebar tree.
  type: z.enum(DAMNodeType),
  shared: z.boolean().optional().nullable(),
  node: nodeSchema,
  with_children: z.boolean(),
  favorite: z.boolean().optional(),
});

export const ancestorsSchema = z.array(
  z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
  }),
);

export type TreeRootNode = z.infer<typeof treeRootNodeSchema>;

/**
 * A list of tree nodes in which an entry that fails to parse is dropped rather than rejecting the
 * whole list.
 *
 * These schemas are consumed through `.parse()` (services/tree.service.ts), so a single
 * unrecognised node used to throw and take the entire sidebar tree with it. The frontend and
 * backend deploy separately here, which makes "the backend added a NodeType" a routine event
 * rather than an impossible one.
 *
 * The trade-off is deliberate and worth stating: this also swallows a node broken for some *other*
 * reason — a missing `node.id`, say — which then presents as a quietly absent folder instead of an
 * error. `unparseable` is therefore logged, so the cause is still recoverable from the console
 * rather than being genuinely silent.
 */
const tolerantNodeArray = <Schema extends z.ZodTypeAny>(schema: Schema) =>
  z.array(z.unknown()).transform((entries) =>
    entries.reduce<Array<z.output<Schema>>>((kept, entry) => {
      const parsed = schema.safeParse(entry);

      if (parsed.success) {
        kept.push(parsed.data);
      } else {
        // warnProd, not warn: the case this exists for is a backend deploy, which only ever happens
        // where dev-only logging is switched off.
        warnProd('Dropped an unparseable tree node', { node: entry, issues: parsed.error.issues });
      }

      return kept;
    }, []),
  );

export type TreeRecursiveChildrenNode = z.infer<typeof treeRootNodeSchema> & {
  children?: Array<TreeRecursiveChildrenNode>;
};
export const treeRecursiveChildrenNodeSchema: z.ZodType<TreeRecursiveChildrenNode> = treeRootNodeSchema.extend({
  children: z.lazy(() => tolerantNodeArray(treeRecursiveChildrenNodeSchema).optional()),
});

export const treeChildrenNodesSchema = tolerantNodeArray(treeRootNodeSchema);

const treeBranchNodeSchema = treeRootNodeSchema.extend({
  children: treeChildrenNodesSchema,
});
export type TreeBranchNode = z.infer<typeof treeBranchNodeSchema>;

export const getTreeRootNodeResponseSchema = tolerantNodeArray(treeRootNodeSchema);
export type GetTreeRootNodeResponse = z.infer<typeof getTreeRootNodeResponseSchema>;

export const getTreeBranchNodeResponseSchema = treeBranchNodeSchema;
export type GetTreeBranchNodeResponse = z.infer<typeof getTreeBranchNodeResponseSchema>;

export const moveNodesResponseSchema = z.object({
  moved: z.array(z.object({ parentFolderNodeId: z.string().optional(), nodes: z.array(z.string()) })),
});
export type MoveNodesResponse = z.infer<typeof moveNodesResponseSchema>;

export type Ancestors = z.infer<typeof ancestorsSchema>;
